import {request} from "./http";
import { normalizeAegisHeroWebpUrl } from "../utils/aegisMedia";

const QUASAR_SETTINGS_LS_KEY = "bs:quasar-settings";
const QUASAR_SETTINGS_PATHS = [
    "/api/me/psychologist-quasar-settings",
    "/api/me/quasar-settings",
];
const PSYCHOLOGIST_CHAT_SETTINGS_PATHS = [
    "/api/me/psychologist-chat-settings",
    "/api/me/psychologist-chat-privacy",
];
const CHAT_INBOUND_POLICY_VALUES = new Set([
    "ANY_AUTHORIZED",
    "ONLY_CLIENTS_WITH_PAID_HISTORY",
    "NOBODY",
]);
const DEFAULT_CHAT_INBOUND_POLICY = "ANY_AUTHORIZED";

function readLocalQuasarSettings() {
    try {
        const raw = localStorage.getItem(QUASAR_SETTINGS_LS_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeLocalQuasarSettings(value) {
    try {
        localStorage.setItem(QUASAR_SETTINGS_LS_KEY, JSON.stringify(value || {}));
    } catch {}
}

function canFallback(err) {
    const st = err?.status ?? err?.response?.status ?? null;
    return st === 404 || st === 405 || st === 501;
}

function isPlainObject(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function mergeUiSettings(base, patch) {
    if (!isPlainObject(base)) return isPlainObject(patch) ? { ...patch } : patch;
    if (!isPlainObject(patch)) return patch;

    const out = { ...base };
    Object.entries(patch).forEach(([key, value]) => {
        out[key] = isPlainObject(value) && isPlainObject(base[key])
            ? mergeUiSettings(base[key], value)
            : value;
    });
    return out;
}

function extractUiSettingsPayload(raw) {
    if (isPlainObject(raw?.item)) return raw.item;
    if (isPlainObject(raw?.data)) return raw.data;
    return isPlainObject(raw) ? raw : {};
}

async function requestFirstAvailable(paths = [], options = {}) {
    let lastErr = null;
    for (const p of paths) {
        try {
            return await request(p, options);
        } catch (err) {
            if (!canFallback(err)) throw err;
            lastErr = err;
        }
    }
    if (lastErr) throw lastErr;
    throw new Error("No endpoint paths configured");
}

function normalizeQuasarSettings(raw = {}) {
    const notifications = raw?.notifications || raw?.preferences || {};
    const bookingPaid =
        raw?.quasarNotifySessionBooked ??
        notifications?.bookingPaid ??
        notifications?.sessionBooked ??
        notifications?.singleOrGroupBookingPaid ??
        raw?.bookingPaid ??
        true;
    const favoriteAdded =
        raw?.quasarNotifyFavoriteAdded ??
        notifications?.favoriteAdded ??
        notifications?.addedToFavorites ??
        raw?.favoriteAdded ??
        true;
    const chatMessage =
        raw?.quasarNotifyChatMessage ??
        notifications?.chatMessage ??
        notifications?.newChatMessage ??
        raw?.chatMessage ??
        true;
    const sessionReminder =
        raw?.quasarNotifySessionReminder ??
        notifications?.sessionReminder ??
        notifications?.reminder ??
        raw?.sessionReminder ??
        true;

    const connectUrl = raw?.quasarConnectUrl ?? raw?.connectUrl ?? "";
    const qrCode = raw?.quasarQrCode ?? raw?.qrCode ?? "";
    const connectExpiresAt = raw?.quasarConnectExpiresAt ?? raw?.connectExpiresAt ?? null;
    const webhookConfiguredRaw = raw?.quasarWebhookConfigured ?? raw?.webhookConfigured;
    const webhookMessage = raw?.quasarWebhookMessage ?? raw?.webhookMessage ?? "";

    const enabledRaw =
        raw?.connected ??
        raw?.quasarConnected ??
        raw?.enabled ??
        raw?.quasarEnabled ??
        raw?.isEnabled;
    const enabled =
        typeof enabledRaw === "boolean"
            ? enabledRaw
            : Boolean(connectUrl || qrCode || webhookConfiguredRaw === true);

    return {
        enabled,
        connectUrl: String(connectUrl || ""),
        qrCode: String(qrCode || ""),
        connectExpiresAt: connectExpiresAt || null,
        webhookConfigured:
            typeof webhookConfiguredRaw === "boolean" ? webhookConfiguredRaw : null,
        webhookMessage: String(webhookMessage || ""),
        notifications: {
            bookingPaid: Boolean(bookingPaid),
            favoriteAdded: Boolean(favoriteAdded),
            chatMessage: Boolean(chatMessage),
            sessionReminder: Boolean(sessionReminder),
        },
    };
}

function toQuasarPayload(settings = {}) {
    const normalized = normalizeQuasarSettings(settings);
    return {
        quasarNotifySessionBooked: Boolean(normalized.notifications.bookingPaid),
        quasarNotifyFavoriteAdded: Boolean(normalized.notifications.favoriteAdded),
        quasarNotifyChatMessage: Boolean(normalized.notifications.chatMessage),
        quasarNotifySessionReminder: Boolean(normalized.notifications.sessionReminder),
    };
}

function toLegacyQuasarPayload(settings = {}) {
    const normalized = normalizeQuasarSettings(settings);
    const payload = {
        enabled: Boolean(normalized.enabled),
        notifications: {
            bookingPaid: Boolean(normalized.notifications.bookingPaid),
            favoriteAdded: Boolean(normalized.notifications.favoriteAdded),
            chatMessage: Boolean(normalized.notifications.chatMessage),
            sessionReminder: Boolean(normalized.notifications.sessionReminder),
        },
    };
    return {
        ...payload,
        bookingPaid: payload.notifications.bookingPaid,
        favoriteAdded: payload.notifications.favoriteAdded,
        chatMessage: payload.notifications.chatMessage,
        sessionReminder: payload.notifications.sessionReminder,
    };
}

function normalizeChatInboundPolicy(raw) {
    const value = String(raw || "").trim().toUpperCase();
    if (CHAT_INBOUND_POLICY_VALUES.has(value)) return value;
    return "";
}

function normalizePsychologistChatSettings(raw = {}) {
    const inboundPolicy = normalizeChatInboundPolicy(
        raw?.inboundPolicy ??
        raw?.directMessagesPolicy ??
        raw?.policy
    );
    const resolved = inboundPolicy || DEFAULT_CHAT_INBOUND_POLICY;

    return {
        inboundPolicy: resolved,
        directMessagesPolicy: resolved,
    };
}

function normalizeGoogleCalendarStatus(raw = {}) {
    const source = raw && typeof raw === "object" ? raw : {};
    return {
        enabled: Boolean(source.enabled),
        configured: Boolean(source.configured),
        connected: Boolean(source.connected),
        accountEmail: String(source.accountEmail || ""),
        calendarId: String(source.calendarId || "primary"),
        calendars: Array.isArray(source.calendars)
            ? source.calendars.map((item) => ({
                id: String(item?.id || ""),
                label: String(item?.label || item?.id || ""),
                primary: Boolean(item?.primary),
            })).filter((item) => item.id)
            : [],
        exportCalendarId: String(source.exportCalendarId || source.calendarId || "primary"),
        busyCalendarIds: Array.isArray(source.busyCalendarIds)
            ? source.busyCalendarIds.map((item) => String(item || "")).filter(Boolean)
            : [],
        syncDirection: String(source.syncDirection || "BIDIRECTIONAL"),
        conflictPolicy: String(source.conflictPolicy || "SKIP_IF_BOOKED"),
        daysBack: Number.isFinite(Number(source.daysBack)) ? Number(source.daysBack) : 7,
        daysForward: Number.isFinite(Number(source.daysForward)) ? Number(source.daysForward) : 180,
        connectedAt: source.connectedAt || null,
        lastSyncedAt: source.lastSyncedAt || null,
        lastSyncStatus: String(source.lastSyncStatus || ""),
        lastSyncMessage: String(source.lastSyncMessage || ""),
    };
}

function normalizeYandexCalendarStatus(raw = {}) {
    const source = raw && typeof raw === "object" ? raw : {};
    return {
        enabled: Boolean(source.enabled),
        configured: Boolean(source.configured),
        connected: Boolean(source.connected),
        accountEmail: String(source.accountEmail || ""),
        calendarUrl: String(source.calendarUrl || ""),
        calendarName: String(source.calendarName || ""),
        calendars: Array.isArray(source.calendars)
            ? source.calendars.map((item) => ({
                id: String(item?.id || ""),
                label: String(item?.label || item?.id || ""),
                primary: Boolean(item?.primary),
            })).filter((item) => item.id)
            : [],
        exportCalendarUrl: String(source.exportCalendarUrl || source.calendarUrl || ""),
        busyCalendarUrls: Array.isArray(source.busyCalendarUrls)
            ? source.busyCalendarUrls.map((item) => String(item || "")).filter(Boolean)
            : [],
        syncDirection: String(source.syncDirection || "BOTH"),
        conflictPolicy: String(source.conflictPolicy || "SKIP_IF_BOOKED"),
        daysBack: Number.isFinite(Number(source.daysBack)) ? Number(source.daysBack) : 7,
        daysForward: Number.isFinite(Number(source.daysForward)) ? Number(source.daysForward) : 180,
        connectedAt: source.connectedAt || null,
        lastSyncedAt: source.lastSyncedAt || null,
        lastSyncStatus: String(source.lastSyncStatus || ""),
        lastSyncMessage: String(source.lastSyncMessage || ""),
    };
}

// Профиль текущего пользователя.
// Если на бэке путь/DTO другой — поменяй тут, остальные компоненты трогать не придётся.
export const profileApi = {
    // email менять нельзя — отправляем только разрешённые поля
    updateMe: ({name, login} = {}) =>
        request("/api/me", {
            method: "PUT",
            json: {name, login},
        }),

    getUiSettings: () => request("/api/me/ui-settings", { method: "GET" }),
    updateUiSettings: async (payload = {}) => {
        let nextPayload = payload;
        try {
            const current = await request("/api/me/ui-settings", { method: "GET" });
            nextPayload = mergeUiSettings(extractUiSettingsPayload(current), payload);
        } catch {
            nextPayload = payload;
        }

        return request("/api/me/ui-settings", {
            method: "PUT",
            json: nextPayload,
        });
    },

    // Смена пароля
    changePassword: ({currentPassword, newPassword, confirmNewPassword} = {}) =>
        request("/api/me/password", {
            method: "PUT",
            json: {currentPassword, newPassword, confirmNewPassword},
        }),

    // Загрузка аватарки (multipart/form-data, field name: image)
    uploadAvatar: (file) => {
        const fd = new FormData();
        fd.append("image", file);
        return request("/api/me/avatar", {method: "POST", body: fd, timeoutMs: 180000})
            .then((res) => {
                if (!res || typeof res !== "object") return res;
                const next = { ...res };
                const rawAvatar =
                    next.avatarUrl ??
                    next.avatar_url ??
                    next.avatar ??
                    next.photoUrl ??
                    next.photo_url ??
                    "";
                const normalizedAvatar = normalizeAegisHeroWebpUrl(rawAvatar);
                if (normalizedAvatar) {
                    next.avatarUrl = normalizedAvatar;
                    if (next.avatar_url != null) next.avatar_url = normalizedAvatar;
                    if (next.avatar != null) next.avatar = normalizedAvatar;
                    if (next.photoUrl != null) next.photoUrl = normalizedAvatar;
                    if (next.photo_url != null) next.photo_url = normalizedAvatar;
                }
                return next;
            });
    },

    deleteAvatar: (id) => {
        return request(`/api/me/avatar?id=${encodeURIComponent(id)}`, { method: "DELETE" });
    },

    // Профиль психолога для self (только для роли PSYCHOLOGIST)
    getMyPsychologistProfile: () => request("/api/me/psychologist-profile"),
    updateMyPsychologistProfile: (dto) =>
        request("/api/me/psychologist-profile", {method: "PUT", json: dto}),
    updateMyPsychologistCover: (coverImageUrl) =>
        request("/api/me/psychologist-profile/cover", {
            method: "PUT",
            json: { coverImageUrl: coverImageUrl || null },
        }),

    getMyPsychologistChatSettings: async () => {
        const res = await requestFirstAvailable(PSYCHOLOGIST_CHAT_SETTINGS_PATHS, { method: "GET" });
        return normalizePsychologistChatSettings(res || {});
    },

    updateMyPsychologistChatSettings: async (patch = {}) => {
        const inboundPolicy = normalizeChatInboundPolicy(
            patch?.inboundPolicy ??
            patch?.directMessagesPolicy
        );

        if (!inboundPolicy) {
            const err = new Error("Политика входящих сообщений не передана.");
            err.status = 400;
            err.code = "CHAT_INBOUND_POLICY_REQUIRED";
            throw err;
        }

        const payload = { inboundPolicy };
        try {
            const res = await request("/api/me/psychologist-chat-settings", {
                method: "PUT",
                json: payload,
            });
            return normalizePsychologistChatSettings(res || payload);
        } catch (err) {
            if (!canFallback(err)) throw err;
            const res = await request("/api/me/psychologist-chat-privacy", {
                method: "PUT",
                json: payload,
            });
            return normalizePsychologistChatSettings(res || payload);
        }
    },

    getMyQuasarSettings: async () => {
        try {
            const res = await requestFirstAvailable(QUASAR_SETTINGS_PATHS, { method: "GET" });
            const normalized = normalizeQuasarSettings(res || {});
            writeLocalQuasarSettings(normalized);
            return normalized;
        } catch (err) {
            if (!canFallback(err)) throw err;
            return normalizeQuasarSettings(readLocalQuasarSettings() || {});
        }
    },

    updateMyQuasarSettings: async (settings = {}) => {
        const modernPayload = toQuasarPayload(settings);
        const legacyPayload = toLegacyQuasarPayload(settings);
        try {
            const res = await request("/api/me/psychologist-quasar-settings", {
                method: "PUT",
                json: modernPayload,
            });
            const normalized = normalizeQuasarSettings(res || modernPayload);
            writeLocalQuasarSettings(normalized);
            return normalized;
        } catch (err) {
            if (!canFallback(err)) throw err;
            try {
                const res = await request("/api/me/quasar-settings", {
                    method: "PUT",
                    json: legacyPayload,
                });
                const normalized = normalizeQuasarSettings(res || legacyPayload);
                writeLocalQuasarSettings(normalized);
                return normalized;
            } catch (err2) {
                if (!canFallback(err2)) throw err2;
                const normalized = normalizeQuasarSettings(modernPayload);
                writeLocalQuasarSettings(normalized);
                return normalized;
            }
        }
    },

    connectMyQuasar: async (settings = {}) => {
        const payload = {};
        const extUserId = String(settings?.extUserId || "").trim();
        const botIdRaw = settings?.botId;
        const botId = Number(botIdRaw);
        if (extUserId) payload.extUserId = extUserId;
        if (Number.isFinite(botId) && botId > 0) payload.botId = botId;
        const connectPaths = QUASAR_SETTINGS_PATHS.map((p) => `${p}/connect`);

        try {
            const res = await requestFirstAvailable(connectPaths, {
                method: "POST",
                json: payload,
            });
            const normalized = normalizeQuasarSettings(res || payload);
            writeLocalQuasarSettings(normalized);
            return normalized;
        } catch (err) {
            if (!canFallback(err)) throw err;
            const normalized = normalizeQuasarSettings({
                enabled: true,
                ...(readLocalQuasarSettings() || {}),
            });
            writeLocalQuasarSettings(normalized);
            return normalized;
        }
    },

    disconnectMyQuasar: async () => {
        const disconnectPaths = QUASAR_SETTINGS_PATHS.map((p) => `${p}/disconnect`);

        try {
            const res = await requestFirstAvailable(disconnectPaths, { method: "POST" });
            const normalized = normalizeQuasarSettings({ ...(res || {}), enabled: false });
            writeLocalQuasarSettings(normalized);
            return normalized;
        } catch (err) {
            if (!canFallback(err)) throw err;
            const normalized = normalizeQuasarSettings({
                ...(readLocalQuasarSettings() || {}),
                enabled: false,
                connectUrl: "",
                qrCode: "",
                connectExpiresAt: null,
            });
            writeLocalQuasarSettings(normalized);
            return normalized;
        }
    },

    getMyGoogleCalendarStatus: async () => {
        const res = await request("/api/me/integrations/google-calendar/status", { method: "GET" });
        return normalizeGoogleCalendarStatus(res || {});
    },

    getMyGoogleCalendarAuthUrl: async (payload = {}) => {
        return request("/api/me/integrations/google-calendar/auth-url", {
            method: "POST",
            json: payload,
        });
    },

    connectMyGoogleCalendar: async ({ code, state } = {}) => {
        const res = await request("/api/me/integrations/google-calendar/connect", {
            method: "POST",
            json: {
                code: String(code || "").trim(),
                state: String(state || "").trim(),
            },
        });
        return normalizeGoogleCalendarStatus(res || {});
    },

    disconnectMyGoogleCalendar: async () => {
        const res = await request("/api/me/integrations/google-calendar/disconnect", { method: "POST" });
        return normalizeGoogleCalendarStatus(res || {});
    },

    saveMyGoogleCalendarPreferences: async (payload = {}) => {
        const res = await request("/api/me/integrations/google-calendar/preferences", {
            method: "POST",
            json: payload,
        });
        return normalizeGoogleCalendarStatus(res || {});
    },

    syncMyGoogleCalendar: (payload = {}) =>
        request("/api/me/integrations/google-calendar/sync", {
            method: "POST",
            json: payload,
        }),

    getMyYandexCalendarStatus: async () => {
        const res = await request("/api/me/integrations/yandex-calendar/status", { method: "GET" });
        return normalizeYandexCalendarStatus(res || {});
    },

    getMyYandexCalendarAuthUrl: async (payload = {}) => {
        return request("/api/me/integrations/yandex-calendar/auth-url", {
            method: "POST",
            json: payload,
        });
    },

    connectMyYandexCalendar: async ({ code, state } = {}) => {
        const res = await request("/api/me/integrations/yandex-calendar/connect", {
            method: "POST",
            json: {
                code: String(code || "").trim(),
                state: String(state || "").trim(),
            },
        });
        return normalizeYandexCalendarStatus(res || {});
    },

    disconnectMyYandexCalendar: async () => {
        const res = await request("/api/me/integrations/yandex-calendar/disconnect", { method: "POST" });
        return normalizeYandexCalendarStatus(res || {});
    },

    saveMyYandexCalendarPreferences: async (payload = {}) => {
        const res = await request("/api/me/integrations/yandex-calendar/preferences", {
            method: "POST",
            json: payload,
        });
        return normalizeYandexCalendarStatus(res || {});
    },

    syncMyYandexCalendar: (payload = {}) =>
        request("/api/me/integrations/yandex-calendar/sync", {
            method: "POST",
            json: payload,
        }),
};
